import React, {useEffect, useState} from 'react';

import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";

import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import {DatasetCategoryDropdown} from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

import {useCreateCategorizedDataset, useGetDefaultDatasetCategory} from "api/api-hooks/datasets-hooks";
import {BaseDatasetDTO} from "api/model/dataset/BaseDatasetDTO";

import {DATASET_ACCESS_TYPES, DATASET_DATA_TYPES, DATASET_DEFAULT_DIALOG_STATE} from "../CreateDatasetDialog";

const CreateSingleDataset = ({setCreateDialogVisibility}) => {
    const [unitSelectorValue, setUnitSelectorValue] = useState(DATASET_DEFAULT_DIALOG_STATE.unitSelectorValue);
    const [datasetTitle, setDatasetTitle] = useState(DATASET_DEFAULT_DIALOG_STATE.datasetTitle)
    const [dataType, setDataType] = useState(DATASET_DEFAULT_DIALOG_STATE.dataType)
    const [accessType, setAccessType] = useState(DATASET_DEFAULT_DIALOG_STATE.accessType)
    const [defaultCategory, setDefaultCategory] = useState<number | undefined>(DATASET_DEFAULT_DIALOG_STATE.defaultCategory)
    const [createdDataset, setCreatedDataset] = useState<BaseDatasetDTO | undefined>(undefined)

    const defaultCategoryQuery = useGetDefaultDatasetCategory();

    useEffect(() => {
        const dataset = sessionStorage.getItem('dataset')
        if (dataset) {
            const parsedDataset = JSON.parse(dataset)
            setUnitSelectorValue(parsedDataset.unit)
            setDatasetTitle(parsedDataset.title)
            setDataType(parsedDataset.dataType)
            setAccessType(parsedDataset.accessType)
            setDefaultCategory(parsedDataset.categoryId)
        }
    }, []);

    useEffect(() => {
        if (defaultCategoryQuery.isFetched) {
            setDefaultCategory(defaultCategoryQuery.data?.id);
        }
        if (defaultCategoryQuery.isError) {
            easasAlert("Error fetching category", '', 'error')
        }
    }, [defaultCategoryQuery.data]);

    const {mutate} = useCreateCategorizedDataset()

    function createDatasetHandler() {
        if (datasetTitle.length === 0) {
            easasAlert('TITLE MISSING', 'Please enter a title', 'error')
            return;
        }

        const data: Partial<BaseDatasetDTO> = {
            title: datasetTitle,
            categoryId: defaultCategory,
            dataType,
            accessType,
            unit: unitSelectorValue
        }

        sessionStorage.setItem('dataset', JSON.stringify(data))

        mutate(data, {
            onSuccess: (data) => {
                setCreatedDataset(data)
                easasAlert('DATASET CREATED', `${data.title} has been created`, 'success')
            },
            onError: (error) => {
                easasAlert('CREATION FAILED', `${error}`, 'error')
                setCreateDialogVisibility(false)
            }
        })
    }

    return (
        <div className='grid'>
            {createdDataset ? (
                    <div className='flex col-12 align-items-center'>
                        <DataTable
                            value={[createdDataset]}
                            className='w-full'
                        >
                            <Column
                                field='title'
                                header='Title'
                                body={e =>
                                    <CopyTextWithButton
                                        text={e.title}
                                        copyableText={e.title}
                                    />}
                            />
                            <Column
                                field="identifier"
                                header="Identifier"
                                body={e =>
                                    <CopyTextWithButton
                                        text={e.identifier}
                                        copyableText={e.identifier}
                                    />}
                            />
                        </DataTable>
                    </div>)
                : (<>
                        <div className='flex col-12 align-items-center'>
                            <p className='col-4 mb-0'>Category</p>
                            <DatasetCategoryDropdown className="w-full" value={defaultCategory}
                                                     onChange={(e) => setDefaultCategory(e)}/>
                        </div>
                        <div className='flex col-12 align-items-center'>
                            <p className='col-4 mb-0'>Dataset Title</p>
                            <InputText
                                className='w-full'
                                value={datasetTitle}
                                onChange={(e) => setDatasetTitle(e.target.value)}
                                placeholder={"New Dataset Title"}
                            />
                        </div>
                        <div className='flex col-12 align-items-center'>
                            <p className='col-4 mb-0'>Dataset Type</p>
                            <Dropdown
                                className='w-full'
                                options={DATASET_DATA_TYPES}
                                value={dataType}
                                onChange={(e) => setDataType(e.value)}
                                showClear
                            />
                        </div>
                        <div className='flex col-12 align-items-center'>
                            <p className='col-4 mb-0'>Data Type</p>
                            <Dropdown
                                className='w-full'
                                options={DATASET_ACCESS_TYPES}
                                value={accessType}
                                onChange={(e) => setAccessType(e.value)}
                                showClear
                            />
                        </div>
                        <div className='flex col-12 align-items-center'>
                            <p className='col-4 mb-0'>Unit</p>
                            <DatasetUnitSelector
                                className='w-full'
                                value={unitSelectorValue}
                                onChange={(e) => setUnitSelectorValue(e.value)}
                            />
                        </div>
                        <div className='flex col-12 gap-2'>
                            <Button className='p-button-success' label='Create' onClick={createDatasetHandler}/>
                            <Button className='p-button-danger' label='Cancel'
                                    onClick={() => setCreateDialogVisibility(false)}/>
                        </div>
                    </>
                )}
        </div>
    );
}

export default CreateSingleDataset;